import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { FiEdit, FiSave } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { ChainBadge } from '@/components/common/ChainBadge'
import { DataTable } from '@/components/common/DataTable'
import type { ActionButton, ColumnConfig } from '@/components/common/DataTable'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { CacheRefreshIconButton, RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useToast } from '@/hooks/useToast'
import {
  useRefreshSystemTokensCache,
  useSystemTokens,
  useUpdateSystemToken,
} from '@/hooks/useSystemConfigData'
import type { SystemToken } from '@/services/admin/systemService'

type TokensTabProps = {
  cardBg: string
}

type TokenFormState = {
  name: string
  contractAddress: string
  decimals: string
  isActive: boolean
  allowNewOrders: boolean
}

const toDateLabel = (value?: string): string => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString()
}

export function TokensTab(props: TokensTabProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const { data, isFetching, refetch } = useSystemTokens()
  const refreshMutation = useRefreshSystemTokensCache()
  const updateMutation = useUpdateSystemToken()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [editingRow, setEditingRow] = useState<SystemToken | null>(null)
  const [filters, setFilters] = useState({
    search: '',
    compatibility: 'all',
    status: 'all' as 'all' | 'active' | 'inactive',
    limit: 100,
  })
  const [form, setForm] = useState<TokenFormState>({
    name: '',
    contractAddress: '',
    decimals: '',
    isActive: true,
    allowNewOrders: true,
  })

  const rows = data ?? []
  const compatibilityOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      ...Array.from(new Set(rows.map(row => row.chainCompatibility))).sort().map(compatibility => ({
        value: compatibility,
        label: compatibility,
      })),
    ],
    [rows, t]
  )
  const statusOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      { value: 'active', label: t('common.active') },
      { value: 'inactive', label: t('common.inactive') },
    ],
    [t]
  )
  const filteredRows = useMemo(() => {
    const needle = filters.search.trim().toLowerCase()
    return rows
      .filter(row => filters.compatibility === 'all' || row.chainCompatibility === filters.compatibility)
      .filter(row => {
        if (filters.status === 'all') return true
        return filters.status === 'active' ? row.isActive : !row.isActive
      })
      .filter(row => {
        if (!needle) return true
        return (
          row.symbol.toLowerCase().includes(needle)
          || row.name.toLowerCase().includes(needle)
          || row.chainCode.toLowerCase().includes(needle)
          || row.chainName.toLowerCase().includes(needle)
          || row.contractAddress.toLowerCase().includes(needle)
        )
      })
      .slice(0, filters.limit)
  }, [filters, rows])

  const columns = useMemo<ColumnConfig<SystemToken>[]>(() => ([
    {
      key: 'symbol',
      title: t('systemConfig.tokens.columns.symbol', { defaultValue: 'Symbol' }),
      width: '7%',
      render: (_, row) => <Text fontSize="xs">{row.symbol}</Text>,
    },
    {
      key: 'chainCode',
      title: t('systemConfig.tokens.columns.chain', { defaultValue: 'Chain' }),
      width: '15%',
      render: (_, row) => (
        <ChainBadge
          chainId={row.chainCode}
          chainCode={row.chainCode}
          chainName={row.chainName}
          isTestnet={row.isTestnet}
        />
      ),
    },
    {
      key: 'name',
      title: t('common.name'),
      width: '14%',
      render: (_, row) => <Text fontSize="xs">{row.name}</Text>,
    },
    {
      key: 'contractAddress',
      title: t('systemConfig.tokens.columns.contractAddress', { defaultValue: 'Token address' }),
      width: '26%',
      render: (_, row) => (
        <Text noOfLines={1} title={row.contractAddress} fontSize="xs">
          {row.contractAddress}
        </Text>
      ),
    },
    {
      key: 'decimals',
      title: t('systemConfig.tokens.columns.decimals', { defaultValue: 'Decimals' }),
      width: '7%',
      align: 'center',
      render: (_, row) => <Text fontSize="xs">{row.decimals}</Text>,
    },
    {
      key: 'isActive',
      title: t('common.status'),
      width: '8%',
      align: 'center',
      render: (_, row) => (
        <Badge colorScheme={row.isActive ? 'green' : 'gray'} fontSize="0.65rem">
          {row.isActive ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
    },
    {
      key: 'allowNewOrders',
      title: t('systemConfig.tokens.columns.allowNewOrders', {
        defaultValue: 'Allow new orders',
      }),
      width: '10%',
      align: 'center',
      render: (_, row) => (
        <Badge colorScheme={row.allowNewOrders ? 'green' : 'gray'} fontSize="0.65rem">
          {row.allowNewOrders ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
    },
    {
      key: 'updatedAt',
      title: t('common.updatedAt', { defaultValue: 'Updated at' }),
      width: '13%',
      render: (_, row) => <Text fontSize="xs">{toDateLabel(row.updatedAt)}</Text>,
      noWrap: true,
    },
  ]), [t])

  const actions = useMemo(() => {
    return (row: SystemToken): ActionButton[] => [
      {
        label: t('common.edit'),
        icon: <FiEdit />,
        onClick: () => {
          setEditingRow(row)
          setForm({
            name: row.name,
            contractAddress: row.contractAddress,
            decimals: String(row.decimals),
            isActive: row.isActive,
            allowNewOrders: row.allowNewOrders,
          })
          onOpen()
        },
      },
    ]
  }, [onOpen, t])

  const handleSave = async () => {
    if (!editingRow) {
      return
    }
    const decimals = Number(form.decimals)
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > 36) {
      toast.error({
        title: t('systemConfig.tokens.invalidDecimals', { defaultValue: 'Decimals must be an integer between 0 and 36' }),
      })
      return
    }
    try {
      await updateMutation.mutateAsync({
        id: editingRow.id,
        payload: {
          name: form.name.trim(),
          contractAddress: form.contractAddress.trim(),
          decimals,
          isActive: form.isActive,
          allowNewOrders: form.allowNewOrders,
        },
      })
      toast.success({
        title: t('common.saveSuccess', { defaultValue: 'Saved' }),
      })
      onClose()
      await refetch()
    } catch (error) {
      toast.error({
        title: t('common.saveFailed', { defaultValue: 'Save failed' }),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const handleRefreshCache = async () => {
    try {
      await refreshMutation.mutateAsync()
      toast.success({
        title: t('systemConfig.actions.refreshCacheSuccess', { defaultValue: 'Cache refreshed' }),
      })
    } catch (error) {
      toast.error({
        title: t('systemConfig.actions.refreshCacheFailed', { defaultValue: 'Cache refresh failed' }),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  return (
    <>
      <Card bg={props.cardBg}>
        <CardHeader>
          <Flex
            direction={{ base: 'column', xl: 'row' }}
            align={{ base: 'stretch', xl: 'end' }}
            justify="space-between"
            gap={3}
          >
            <HStack spacing={3} align="end" flexWrap={{ base: 'wrap', xl: 'nowrap' }} flex="1" minW={0}>
              <Heading size="sm" minW="fit-content">
                {t('systemConfig.tabs.tokens', { defaultValue: 'tokens' })}
              </Heading>
              <FormControl minW={{ base: '100%', md: '220px' }} maxW={{ xl: '220px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('common.search', { defaultValue: 'Search' })}
                </FormLabel>
                <Input
                  size="xs"
                  value={filters.search}
                  onChange={event => setFilters(prev => ({ ...prev, search: event.target.value }))}
                  placeholder={t('common.search', { defaultValue: 'Search' })}
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '130px' }} maxW={{ xl: '130px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('systemConfig.chains.columns.compatibility', { defaultValue: 'Compatibility' })}
                </FormLabel>
                <DropdownSelect
                  value={filters.compatibility}
                  options={compatibilityOptions}
                  onChange={value => setFilters(prev => ({ ...prev, compatibility: value }))}
                  size="xs"
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '120px' }} maxW={{ xl: '120px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('common.status')}
                </FormLabel>
                <DropdownSelect
                  value={filters.status}
                  options={statusOptions}
                  onChange={value =>
                    setFilters(prev => ({ ...prev, status: value as typeof filters.status }))
                  }
                  size="xs"
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '100px' }} maxW={{ xl: '100px' }}>
                <FormLabel fontSize="xs" mb={1}>{t('common.limit')}</FormLabel>
                <DropdownSelect
                  value={String(filters.limit)}
                  options={[50, 100, 200, 500].map(limit => ({
                    value: String(limit),
                    label: String(limit),
                  }))}
                  onChange={value => setFilters(prev => ({ ...prev, limit: Number(value) }))}
                  size="xs"
                />
              </FormControl>
            </HStack>
            <HStack spacing={2} justify={{ base: 'flex-start', xl: 'flex-end' }} flexShrink={0}>
              <RefreshIconButton
                label={t('common.refresh')}
                onClick={() => refetch()}
                isLoading={isFetching}
              />
              <CacheRefreshIconButton
                label={t('systemConfig.actions.refreshCache', { defaultValue: 'Refresh Cache' })}
                onClick={handleRefreshCache}
                isLoading={refreshMutation.isPending}
              />
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody
          pt={0}
          sx={{
            'th, td': {
              px: 2,
              py: 2,
            },
            th: {
              fontSize: 'xs',
            },
          }}
        >
          <DataTable<SystemToken>
            data={filteredRows}
            loading={isFetching}
            columns={columns}
            actions={actions}
            actionsColumnWidth="64px"
            showFilterBar={false}
            showRefresh={false}
            emptyMessage={t('common.noData', { defaultValue: 'No data' })}
          />
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader>{t('systemConfig.tokens.edit', { defaultValue: 'Edit token' })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>{t('common.name')}</FormLabel>
                <Input
                  value={form.name}
                  onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('systemConfig.tokens.fields.contractAddress', { defaultValue: 'Token address' })}</FormLabel>
                <Input
                  value={form.contractAddress}
                  onChange={event => setForm(prev => ({ ...prev, contractAddress: event.target.value }))}
                />
              </FormControl>
              <HStack align="start" spacing={4}>
                <FormControl>
                  <FormLabel>{t('systemConfig.tokens.fields.decimals', { defaultValue: 'Decimals' })}</FormLabel>
                  <Input
                    value={form.decimals}
                    onChange={event => setForm(prev => ({ ...prev, decimals: event.target.value }))}
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb={0}>{t('common.active')}</FormLabel>
                  <Switch
                    isChecked={form.isActive}
                    onChange={event => setForm(prev => ({ ...prev, isActive: event.target.checked }))}
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb={0}>
                    {t('systemConfig.tokens.fields.allowNewOrders', {
                      defaultValue: 'Allow new orders',
                    })}
                  </FormLabel>
                  <Switch
                    isChecked={form.allowNewOrders}
                    onChange={event =>
                      setForm(prev => ({ ...prev, allowNewOrders: event.target.checked }))
                    }
                  />
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button variant="ghost" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="admin"
                leftIcon={<FiSave />}
                onClick={handleSave}
                isLoading={updateMutation.isPending}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
